<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="deliveryTypes"
      :headers="tableHeaders"
      :list="deliveryTypes"
      @add-new="onAddNew"
      @deleteMultiselect="onDeleteMultiselect"
      table-id="deliveryTypes"
      @refresh-data="refreshData"
      clickable
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DeliveryType)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <DeliveryTypeModalCreateEdit
      v-if="createEditModalActive"
      :deliveryType="deliveryType"
      title="Add Delivery Type"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { getDeliveryTypes, removeDeliveryTypesByIds } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import DeliveryTypeModalCreateEdit from '@/components/documents/DeliveryTypeModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type DeliveryType, eDeliveryType } from '@/types/DeliveryTypes';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const { reloadLookups } = useLookupsStore();
const deliveryTypes = ref<DeliveryType[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'eTypeString', text: 'EType', type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeDeliveryType(): DeliveryType {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    isFromManager: true,
    isActive: false,
    eType: 0,
  };
}

onMounted(async () => {
  await loadData();
});

async function loadData() {
  const res = await getDeliveryTypes();
  deliveryTypes.value = populateDeliveryTypeField(res);
}

function getDeliveryTypeString(deliveryType: number): string {
  switch (deliveryType) {
    case eDeliveryType.Courier:
      return 'Courier';
    case eDeliveryType.TakeAway:
      return 'TakeAway';
    case eDeliveryType.DropShipping:
      return 'DropShipping';
    case eDeliveryType.Post:
      return 'Post';
    case eDeliveryType.Transport:
      return 'Transport';
    default:
      return 'Unknown';
  }
}

function populateDeliveryTypeField(deliveryTypes: DeliveryType[]): DeliveryType[] {
  return deliveryTypes.map((deliveryType) => ({
    ...deliveryType,
    eTypeString: deliveryType.eType != null ? getDeliveryTypeString(deliveryType.eType) : null,
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: DeliveryType[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeDeliveryTypesByIds(idsToDelete);
  if (!res && deliveryTypes.value)
    deliveryTypes.value = deliveryTypes.value.filter(
      (deliveryType) => !idsToDelete.includes(deliveryType.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['deliveryTypes']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDeliveryTypesByIds([idToDelete]);
  if (!res && deliveryTypes.value)
    deliveryTypes.value = deliveryTypes.value.filter(
      (deliveryType) => deliveryType.id !== idToDelete
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['deliveryTypes']);
}

const deliveryType = ref<DeliveryType>(initializeDeliveryType());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNew() {
  deliveryType.value = initializeDeliveryType();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEdit(item: DeliveryType) {
  deliveryType.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: DeliveryType) {
  if (!deliveryTypes.value) return;

  const index = deliveryTypes.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) deliveryTypes.value[index] = modifiedData;
  else deliveryTypes.value.push(modifiedData);
  deliveryTypes.value = populateDeliveryTypeField(deliveryTypes.value);
  createEditModalActive.value = false;

  await reloadLookups(['deliveryTypes']);
}

async function refreshData(data: TableFilterData) {
  await loadData();

  if (data.query && deliveryTypes.value) {
    deliveryTypes.value = filterData(deliveryTypes.value, data.query, [
      'name',
      'code',
      'eTypeString',
    ]);
  }

  if (data.column !== undefined && data.order && deliveryTypes.value) {
    deliveryTypes.value = sortData(deliveryTypes.value, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined && deliveryTypes.value)
    deliveryTypes.value = filterActive(deliveryTypes.value, data.isActive);
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DeliveryType>();
function onDeleteRow(item: DeliveryType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
